import React, { useLayoutEffect, useRef, useState } from "react";
import Select from "react-select";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { CheckCircle, Clock, Hourglass, PhoneCall, XCircle } from "lucide-react";

const CallPerformanceChart = () => {
  const chartRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState("All");

  const chartData = {
    Priti: [
      { month: "Jan", converted: 50, inprogress: 35, pending: 30, notinterested: 25 },
      { month: "Feb", converted: 45, inprogress: 30, pending: 15, notinterested: 10 },
      { month: "Mar", converted: 50, inprogress: 20, pending: 20, notinterested: 10 },
      { month: "Apr", converted: 55, inprogress: 25, pending: 10, notinterested: 10 },
      { month: "May", converted: 60, inprogress: 20, pending: 10, notinterested: 10 },
      { month: "Jun", converted: 65, inprogress: 15, pending: 10, notinterested: 10 },
    ],
    Swati: [
      { month: "Jan", converted: 35, inprogress: 30, pending: 25, notinterested: 10 },
      { month: "Feb", converted: 50, inprogress: 25, pending: 15, notinterested: 10 },
      { month: "Mar", converted: 45, inprogress: 35, pending: 10, notinterested: 10 },
      { month: "Apr", converted: 55, inprogress: 25, pending: 10, notinterested: 10 },
      { month: "May", converted: 60, inprogress: 20, pending: 10, notinterested: 10 },
      { month: "Jun", converted: 70, inprogress: 15, pending: 10, notinterested: 5 },
    ],
    Akash: [
      { month: "Jan", converted: 30, inprogress: 40, pending: 20, notinterested: 10 },
      { month: "Feb", converted: 55, inprogress: 25, pending: 10, notinterested: 10 },
      { month: "Mar", converted: 50, inprogress: 30, pending: 10, notinterested: 10 },
      { month: "Apr", converted: 60, inprogress: 25, pending: 10, notinterested: 5 },
      { month: "May", converted: 65, inprogress: 20, pending: 10, notinterested: 5 },
      { month: "Jun", converted: 70, inprogress: 15, pending: 10, notinterested: 5 },
    ],
  };

  const getAllCombined = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    return months.map((month, index) => {
      const totals = { converted: 0, inprogress: 0, pending: 0, notinterested: 0 };

      Object.values(chartData).forEach((userData) => {
        const row = userData[index];
        totals.converted += row.converted;
        totals.inprogress += row.inprogress;
        totals.pending += row.pending;
        totals.notinterested += row.notinterested;
      });

      return { month, ...totals };
    });
  };

  const displayData = selectedUser === "All" ? getAllCombined() : chartData[selectedUser];

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        paddingLeft: 0,
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        renderer: am5xy.AxisRendererY.new(root, { inversed: true }),
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererX.new(root, {}),
      })
    );

    const colors = {
      converted: am5.color(0x22c55e),
      inprogress: am5.color(0x3b82f6),
      pending: am5.color(0xfacc15),
      notinterested: am5.color(0xef4444),
    };

    function makeSeries(name, field, color) {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name,
          stacked: true,
          xAxis,
          yAxis,
          valueXField: field,
          categoryYField: "month",
        })
      );

      series.columns.template.setAll({
        fill: color,
        stroke: color,
        tooltipText: "{name}: {valueX}",
      });

      return series;
    }

    const seriesDefs = [
      { name: "Converted", field: "converted", color: colors.converted },
      { name: "In Progress", field: "inprogress", color: colors.inprogress },
      { name: "Pending", field: "pending", color: colors.pending },
      { name: "Not Interested", field: "notinterested", color: colors.notinterested },
    ];

    const seriesArr = seriesDefs.map((def) => makeSeries(def.name, def.field, def.color));

    yAxis.data.setAll(displayData);
    seriesArr.forEach((s) => s.data.setAll(displayData));

    chart.appear(1000, 100);

    return () => root.dispose();
  }, [selectedUser]);

  const userOptions = [
    { value: "All", label: "All" },
    { value: "Priti", label: "Priti" },
    { value: "Swati", label: "Swati" },
    { value: "Akash", label: "Akash" },
  ];

  // Compute totals for summary cards
  const totals = displayData.reduce(
    (acc, row) => {
      acc.converted += row.converted;
      acc.inprogress += row.inprogress;
      acc.pending += row.pending;
      acc.notinterested += row.notinterested;
      return acc;
    },
    { converted: 0, inprogress: 0, pending: 0, notinterested: 0 }
  );

  const totalCalls = totals.converted + totals.inprogress + totals.pending + totals.notinterested;

  return (
    <div className="bg-white p-4 rounded-xl shadow-md">
      <h3 className="dashboard-title">Call Performance Chart</h3>

      {/* Dropdown */}
      <div className="flex justify-center mb-4 w-60 mx-auto">
        <Select
          options={userOptions}
          value={userOptions.find((opt) => opt.value === selectedUser)}
          onChange={(selected) => setSelectedUser(selected.value)}
          className="w-full"
        />
      </div>

      {/* SUMMARY CARDS */}
      <div className="flex gap-3 mb-4 overflow-x-auto pb-2 no-scrollbar">
        {/* Total Calls - different color */}
        <div className="flex-shrink-0 min-w-[200px] p-3 rounded-lg flex items-center gap-3 bg-purple-100">
          <div className="p-2 rounded-full bg-purple-200">
            <PhoneCall className="text-purple-700" size={26} />
          </div>
          <div>
            <p className="font-semibold text-purple-800">Total Calls</p>
            <p className="text-xl font-bold text-purple-900">{totalCalls}</p>
          </div>
        </div>

        {/* Converted */}
        <div className="flex-shrink-0 min-w-[200px] p-3 rounded-lg flex items-center gap-3 bg-green-100">
          <div className="p-2 rounded-full bg-green-200">
            <CheckCircle className="text-green-700" size={26} />
          </div>
          <div>
            <p className="font-semibold text-green-800">Converted</p>
            <p className="text-xl font-bold text-green-900">{totals.converted}</p>
          </div>
        </div>

        {/* In Progress */}
        <div className="flex-shrink-0 min-w-[200px] p-3 rounded-lg flex items-center gap-3 bg-blue-100">
          <div className="p-2 rounded-full bg-blue-200">
            <Clock className="text-blue-700" size={26} />
          </div>
          <div>
            <p className="font-semibold text-blue-800">In Progress</p>
            <p className="text-xl font-bold text-blue-900">{totals.inprogress}</p>
          </div>
        </div>

        {/* Pending */}
        <div className="flex-shrink-0 min-w-[200px] p-3 rounded-lg flex items-center gap-3 bg-yellow-100">
          <div className="p-2 rounded-full bg-yellow-200">
            <Hourglass className="text-yellow-700" size={26} />
          </div>
          <div>
            <p className="font-semibold text-yellow-800">Pending</p>
            <p className="text-xl font-bold text-yellow-900">{totals.pending}</p>
          </div>
        </div>

        {/* Not Interested */}
        <div className="flex-shrink-0 min-w-[200px] p-3 rounded-lg flex items-center gap-3 bg-red-100">
          <div className="p-2 rounded-full bg-red-200">
            <XCircle className="text-red-700" size={26} />
          </div>
          <div>
            <p className="font-semibold text-red-800">Not Interested</p>
            <p className="text-xl font-bold text-red-900">{totals.notinterested}</p>
          </div>
        </div>
      </div>



      {/* Chart */}
      <div ref={chartRef} className="w-full h-[400px]" />
    </div>
  );
};

export default CallPerformanceChart;
